import { useEffect, useRef } from "preact/hooks";

const keyHandlerRegistry = new Map();
let nextHandlerId = 1;

function getRegistryEntriesForKey(key) {
  if (!keyHandlerRegistry.has(key)) {
    keyHandlerRegistry.set(key, {
      entries: [],
      listening: false,
      listener: null
    });
  }

  return keyHandlerRegistry.get(key);
}

function sortEntries(entries) {
  entries.sort((left, right) => {
    const leftPriority = left.priorityRef.current;
    const rightPriority = right.priorityRef.current;

    if (leftPriority !== rightPriority) {
      return leftPriority - rightPriority;
    }

    return left.id - right.id;
  });
}

function matchesKeyEvent(key, event) {
  if (event.key === key) {
    return true;
  }

  if (key === "/" && event.code === "Slash") {
    return true;
  }

  return false;
}

function ensureKeyListener(key) {
  const registry = getRegistryEntriesForKey(key);
  if (registry.listening || typeof window === "undefined") {
    return;
  }

  registry.listener = (event) => {
    if (!matchesKeyEvent(key, event)) {
      return;
    }

    for (let index = registry.entries.length - 1; index >= 0; index -= 1) {
      const entry = registry.entries[index];
      if (!entry?.enabledRef.current) {
        continue;
      }

      if (!entry.handlerRef.current?.(event)) {
        continue;
      }

      event.preventDefault();
      event.stopPropagation();
      return;
    }
  };

  window.addEventListener("keydown", registry.listener, true);
  registry.listening = true;
}

function cleanupKeyListener(key) {
  const registry = getRegistryEntriesForKey(key);
  if (!registry.listening || typeof window === "undefined" || registry.entries.length > 0) {
    return;
  }

  window.removeEventListener("keydown", registry.listener, true);
  registry.listener = null;
  registry.listening = false;
}

function useKeyStack(key, onKey, enabled = true, priority = 0) {
  const handlerRef = useRef(onKey);
  const enabledRef = useRef(Boolean(enabled));
  const priorityRef = useRef(priority);

  handlerRef.current = onKey;
  enabledRef.current = Boolean(enabled);
  priorityRef.current = priority;

  useEffect(() => {
    const registry = getRegistryEntriesForKey(key);
    const entry = {
      id: nextHandlerId++,
      handlerRef,
      enabledRef,
      priorityRef
    };

    registry.entries.push(entry);
    sortEntries(registry.entries);
    ensureKeyListener(key);

    return () => {
      const entryIndex = registry.entries.findIndex((candidate) => candidate.id === entry.id);
      if (entryIndex >= 0) {
        registry.entries.splice(entryIndex, 1);
      }

      cleanupKeyListener(key);
    };
  }, [key]);
}

export function useEscapeStack(onEscape, enabled = true, priority = 0) {
  useKeyStack("Escape", onEscape, enabled, priority);
}

export function useEnterStack(onEnter, enabled = true, priority = 0) {
  useKeyStack("Enter", onEnter, enabled, priority);
}

export function useSlashStack(onSlash, enabled = true, priority = 0) {
  useKeyStack("/", onSlash, enabled, priority);
}
